export const FUNCTIONAL_STATUS = Object.freeze({
  ACTIVE: { label: "Actif", actionLabel: "Utiliser maintenant" },
  BETA: { label: "Bêta", actionLabel: "Essayer la bêta" },
  PRIVATE_BETA: { label: "Bêta privée", actionLabel: "Voir les conditions d’accès" },
  READ_ONLY: { label: "Lecture seule", actionLabel: "Consulter" },
  COMING_SOON: { label: "Fonction en préparation", actionLabel: "Voir la présentation" },
  PROSPECTIVE: { label: "Prospectif", actionLabel: "Découvrir" },
  DISABLED: { label: "Désactivé", actionLabel: "Indisponible" },
  RETIRED: { label: "Retiré", actionLabel: "Consulter l’historique" },
});

export const CONTENT_NATURE = Object.freeze({
  science: "Science vérifiée",
  prospective: "Hypothèse prospective",
  narrative: "Univers narratif Eternua",
  vision_3024: "Vision future 3024",
});

// No product fallback is kept in the client: the server registry is authoritative.
// An empty registry is safer than displaying a stale promise when the API is down.
export const FALLBACK_FEATURES = Object.freeze([]);

const LEGACY_STATUS_ALIASES = Object.freeze({
  available: "ACTIVE",
  beta: "BETA",
  coming_soon: "COMING_SOON",
  future: "PROSPECTIVE",
});

export function normalizeFeature(feature) {
  const requestedStatus = LEGACY_STATUS_ALIASES[feature?.functionalStatus] || feature?.functionalStatus;
  const functionalStatus = FUNCTIONAL_STATUS[requestedStatus] ? requestedStatus : "DISABLED";
  const collectedData = Array.isArray(feature?.collectedData) ? feature.collectedData.filter(Boolean) : [];
  return {
    ...feature,
    featureKey: String(feature?.featureKey || ""),
    functionalStatus,
    functionalStatusLabel: FUNCTIONAL_STATUS[functionalStatus].label,
    contentNature: String(feature?.contentNature || ""),
    contentNatureLabel: CONTENT_NATURE[feature?.contentNature] || "",
    availableActions: Array.isArray(feature?.availableActions) ? feature.availableActions.filter(Boolean) : [],
    limitations: Array.isArray(feature?.limitations) ? feature.limitations.filter(Boolean) : [],
    workingCapabilities: Array.isArray(feature?.workingCapabilities) ? feature.workingCapabilities.filter(Boolean) : [],
    simulatedCapabilities: Array.isArray(feature?.simulatedCapabilities) ? feature.simulatedCapabilities.filter(Boolean) : [],
    collectedData,
    collectsData: Boolean(feature?.collectsData ?? collectedData.length),
    localOnly: Boolean(feature?.localOnly),
    requiresAccount: Boolean(feature?.requiresAccount),
    handlesMoney: Boolean(feature?.handlesMoney),
    isProspective: Boolean(feature?.isProspective),
    pathPrefixes: Array.isArray(feature?.pathPrefixes) ? feature.pathPrefixes.filter(Boolean) : [],
    apiPrefixes: Array.isArray(feature?.apiPrefixes) ? feature.apiPrefixes.filter(Boolean) : [],
    navigationVisible: feature?.navigationVisible !== false,
    sitemapVisible: feature?.sitemapVisible !== false,
    targetDate: feature?.targetDate || null,
    lastTestedAt: feature?.lastTestedAt || null,
    publicNotice: String(feature?.publicNotice || ""),
    rollbackPlan: String(feature?.rollbackPlan || ""),
    owner: String(feature?.owner || ""),
  };
}

export function normalizeFeatures(features) {
  return (Array.isArray(features) ? features : [])
    .map(normalizeFeature)
    .filter((feature) => feature.featureKey)
    .sort((a, b) => Number(a.displayOrder || 100) - Number(b.displayOrder || 100));
}

export function findFeatureForPath(features, pathname) {
  const cleanPath = String(pathname || "/").split(/[?#]/)[0];
  const matches = [];
  features.forEach((feature) => {
    (feature.pathPrefixes || []).forEach((prefix) => {
      if (cleanPath === prefix || cleanPath.startsWith(`${prefix}/`) || cleanPath.startsWith(`${prefix}-`)) {
        matches.push({ feature, length: prefix.length });
      }
    });
  });
  matches.sort((a, b) => b.length - a.length);
  return matches[0]?.feature || null;
}

export function isFeatureNavigable(feature) {
  return Boolean(feature?.navigationVisible) && !["DISABLED", "RETIRED"].includes(feature?.functionalStatus);
}

export function isFeatureSitemapEligible(feature) {
  return Boolean(feature?.sitemapVisible) && ["ACTIVE", "BETA", "READ_ONLY", "PROSPECTIVE"].includes(feature?.functionalStatus);
}

export function featureMatchesQuery(feature, query) {
  const clean = String(query || "").trim().toLocaleLowerCase("fr");
  if (!clean) return true;
  return [
    feature.title,
    feature.description,
    feature.statusExplanation,
    feature.contentNatureLabel,
    feature.publicNotice,
    ...(feature.workingCapabilities || []),
  ].join(" ").toLocaleLowerCase("fr").includes(clean);
}

export function featureForSearchItem(features, item) {
  const key = String(item?.universe?.key || "").toLowerCase();
  if ([
    "community_groups", "community_events", "community_projects",
    // Transitional aliases for already cached search documents.
    "community_group", "community_event", "community_project",
  ].includes(key)) {
    return features.find((feature) => feature.featureKey === "today-community") || null;
  }
  if (key === "emploi") return features.find((feature) => feature.featureKey === "today-employment") || null;
  if (key === "services") return features.find((feature) => feature.featureKey === "today-services") || null;
  if (["sorties", "outing", "events"].includes(key)) return features.find((feature) => feature.featureKey === "today-outings") || null;
  if (["animaux", "animals"].includes(key)) return features.find((feature) => feature.featureKey === "today-animals") || null;
  if (key === "marketplace") return features.find((feature) => feature.featureKey === "today-buy") || null;
  return null;
}
